import { assignableLevels, RoleLevels } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { Role } from '@blisspointmedia/bpm-types/dist/Users';

/**
 * Plain-language names for the role levels a chart or dashboard can require.
 *
 * Labels and policy only. The RULE — `canView`, `canEdit`, `assignableLevels`, `applyCeiling` —
 * lives in `@blisspointmedia/bpm-types/dist/dashboarding` and is shared with the backend; nothing in
 * this file decides who may do what.
 *
 * THE SCALE IS INVERTED, AND THIS FILE IS WHERE THAT MEETS SOMEBODY WHO IS NOT AN ENGINEER. A lower
 * ROLE number grants more access, so a higher LEVEL admits a WIDER audience: level 0 is the
 * narrowest setting and level 10 the widest. Every label below therefore reads as a widening
 * sentence as the number grows, and a control that lists levels in ascending order is listing them
 * from the smallest audience to the largest.
 *
 * NAMEABLE IS NOT THE SAME AS OFFERED. This map names every level on the scale, including the ones
 * a user cannot choose — see `OFFERED_LEVELS` below. A record can hold a level that nothing offers,
 * so anything that can reach the screen needs a name here.
 */
const ROLE_LEVEL_LABELS: Record<number, string> = {
  [Role.ADMIN]: 'Admins only',
  [Role.INTERNAL]: 'Tinuiti internal only',
  [Role.INTERNAL_RESTRICTED]: 'Tinuiti internal, including restricted',
  [Role.NOC]: 'Tinuiti internal, including NOC',
  [Role.PARTNER]: 'Tinuiti internal, including partners',
  [Role.EXTERNAL]: 'Everyone with access to this client'
};

/**
 * The three audiences a user can name in chat, and the two records they can name them for.
 *
 * THIS IS A CROSS-REPOSITORY CONTRACT. These exact strings are `SHARING_AUDIENCES` and
 * `SHARING_TARGETS` in `tinuiti-dashboard-charts-agent` (`app/agent/tools/charting.py`). A drift
 * between the two sides is SILENT: `extractSharingRequest` rejects an unrecognised value, so the
 * user's request does nothing — no error, no message, no write. The test asserting these literals is
 * the only guard in CI.
 *
 * NAMES, NOT NUMBERS, ON PURPOSE. The scale is inverted (see the note above `ROLE_LEVEL_LABELS`),
 * and an audience name has no direction to get backwards. Every conversion between a name and a
 * level happens in this file and nowhere else.
 */
export const SharingAudience = {
  ADMINS_ONLY: 'admins_only',
  INTERNAL_ONLY: 'internal_only',
  EVERYONE_WITH_CLIENT_ACCESS: 'everyone_with_client_access'
} as const;
export type SharingAudienceType = (typeof SharingAudience)[keyof typeof SharingAudience];

export const SharingRequestTarget = {
  CHART: 'chart',
  DASHBOARD: 'dashboard'
} as const;
export type SharingRequestTargetType =
  (typeof SharingRequestTarget)[keyof typeof SharingRequestTarget];

/**
 * The one mapping from an audience name to the role level that admits it.
 *
 * THE SINGLE SOURCE OF TRUTH FOR WHICH LEVELS THIS PRODUCT OFFERS. `OFFERED_LEVELS` below is derived
 * from it, so adding an audience here widens both the sharing control and what chat can ask for, in
 * one edit. Written the other way round -- a hand-written set beside a hand-written map -- the two
 * drift, and the drift is invisible: the control offers a tier the agent has no name for, so chat
 * silently cannot set the very level a user just picked by hand.
 */
const LEVEL_BY_AUDIENCE: Record<SharingAudienceType, number> = {
  [SharingAudience.ADMINS_ONLY]: Role.ADMIN,
  [SharingAudience.INTERNAL_ONLY]: Role.INTERNAL,
  [SharingAudience.EVERYONE_WITH_CLIENT_ACCESS]: Role.EXTERNAL
};

/**
 * The three levels a user may CHOOSE: this agency's administrators, everyone inside Tinuiti, or
 * everyone who can already reach this client's account.
 *
 * Three rather than the whole scale is a product decision, not a rule. The scale holds more levels
 * and the backend still enforces all of them; a user simply is not asked to reason about the middle
 * tiers. Offering one means adding it to `LEVEL_BY_AUDIENCE` above — this set follows, and so does
 * what chat can ask for. Nothing else changes.
 */
const OFFERED_LEVELS: ReadonlySet<number> = new Set(Object.values(LEVEL_BY_AUDIENCE));

/**
 * The audience a level admits, in words.
 *
 * A level off the scale gets a generated label rather than a blank: real accounts carry values the
 * `Role` object does not name — 4 is the one that exists today — and a record written by such an
 * account holds that number. See the note above `Role` in bpm-types for why the columns are plain
 * `number` and not `RoleType`.
 *
 * "AND MORE PRIVILEGED", NEVER "AND ABOVE". A level of 4 admits every role from 0 to 4, which is
 * everyone at or more privileged than role 4 — the opposite of what "4 and above" reads as to
 * anybody who has not internalised the inverted scale.
 */
export const roleLevelLabel = (level: number): string =>
  ROLE_LEVEL_LABELS[level] ?? `Role ${level} and more privileged`;

/**
 * The levels a sharing control should list, narrowed to the three this product offers.
 *
 * Wraps `assignableLevels`, so the shared rule still decides what is legal: the list never runs
 * below the caller's own role (nobody may build a record they could not then open), and `ceiling`
 * still caps an edit column at the audience that can see the record.
 *
 * `current` SURVIVES THE NARROWING. A record can hold a level this product does not offer: a row
 * written before the set was narrowed to three, or one clamped to an off-scale role such as 4. Drop
 * that level from the list and the control has no option matching the record's own state — the
 * toggle falls back to printing a bare number, and a user who came only to change the view level is
 * shown a lie about the edit level. So the list is the three offered levels plus whatever the record
 * actually holds, and the extra row disappears as soon as somebody moves off it. (Same reason
 * `assignableLevels` folds `current` in — see its own note.)
 *
 * A NEW record does not need that allowance. The backend's create defaults are view 10 and
 * `max(callerRole, Role.INTERNAL)`, and both of those levels are offered, so a fresh chart opens on
 * exactly three rows. That was not true while the edit default was floored at NOC — every new chart
 * was then born on a level nobody could choose.
 */
export const assignableRoleLevels = (
  userRole: number,
  current?: number,
  ceiling?: number
): number[] =>
  assignableLevels(userRole, current, ceiling).filter(
    level => OFFERED_LEVELS.has(level) || level === current
  );

/**
 * One chat-driven sharing request, after validation.
 *
 * Each column is OPTIONAL, and its absence is meaningful: the backend keeps the stored level for a
 * column a request omits, so "make it internal only" must leave `editableBy` out rather than restate
 * it. See the note beside `SaveChartOptions` in chart-save.ts.
 */
export interface SharingRequest {
  target: SharingRequestTargetType;
  visibleTo?: SharingAudienceType;
  editableBy?: SharingAudienceType;
}

export const isSharingAudience = (value: unknown): value is SharingAudienceType =>
  Object.values(SharingAudience).some(audience => audience === value);

export const isSharingRequestTarget = (value: unknown): value is SharingRequestTargetType =>
  Object.values(SharingRequestTarget).some(target => target === value);

export const audienceToLevel = (audience: SharingAudienceType): number =>
  LEVEL_BY_AUDIENCE[audience];

/**
 * The audience a level admits, or `undefined` when this product has no name for that level.
 *
 * `undefined` IS A REAL ANSWER, not a failure. A record can hold a level nothing offers: 2 and 3 sit
 * on the scale but are not offered, and at least one real account carries the off-scale role 4. A
 * caller describing such a record falls back to `roleLevelLabel`, which names every level.
 */
export const levelToAudience = (level: number): SharingAudienceType | undefined =>
  Object.values(SharingAudience).find(audience => LEVEL_BY_AUDIENCE[audience] === level);

/**
 * A validated sharing request as the two role levels `saveChart` takes.
 *
 * A column the request omitted stays ABSENT rather than becoming `undefined`, because absence is what
 * tells the backend to keep the stored level — or, on a create, to apply its own default. Sending an
 * explicit `undefined` would serialise as a missing key too, but building the object this way makes
 * the intent legible and matches `SaveChartOptions`' own note.
 */
export const requestToRoleLevels = (request: SharingRequest): RoleLevels => ({
  ...(request.visibleTo === undefined
    ? {}
    : { viewRoleRequired: audienceToLevel(request.visibleTo) }),
  ...(request.editableBy === undefined
    ? {}
    : { editRoleRequired: audienceToLevel(request.editableBy) })
});

/**
 * Whether every audience a request NAMES is one this caller may actually assign.
 *
 * The first of the two rules a request has to clear, and the only one that can be checked a column
 * at a time: `assignableRoleLevels` never runs below the caller's own role, so a user cannot create
 * a record they could not then open. An omitted column is vacuously fine — absence means "leave
 * this one alone", so it can never be the thing that refuses a request.
 *
 * The CEILING is the other rule, and it needs both columns at once — see `resolveRequestedLevels` in
 * sharing-request-utils.ts, which composes this with it. Checked here rather than at each call site
 * because both chat surfaces ask it and a drift between them is a silently different promise.
 */
export const isRequestAssignable = (request: SharingRequest, userRole: number): boolean =>
  [request.visibleTo, request.editableBy].every(
    audience =>
      audience === undefined || assignableRoleLevels(userRole).includes(audienceToLevel(audience))
  );
